// Lib
import { createApi } from "@reduxjs/toolkit/query/react";
// Types
import { UrlQueryParams } from "types/common";
import {
  CustomerProfileResponseDto,
  GetCustomerCouponsResponseDto,
  GetCustomerMenuResponseDto,
  GetCustomersAddressResponseDto,
  GetCustomersResponseDto,
} from "types/customers";
// Constants
import { endpoints } from "consts";
// Helpers
import { customersUrlBuilder } from "./urlBuilder/customers";
// Utils
import { coreQuery } from "utils/baseQuery";
import {
  couponsMapper,
  customerMapper,
  customerMenuMapper,
  customersMapper,
} from "utils/mappers";

export const customersAPI = createApi({
  reducerPath: "customersAPI",
  baseQuery: coreQuery(),
  tagTypes: ["customers", "customer"],

  endpoints: build => ({
    getCustomers: build.query<
      GetCustomersResponseDto,
      { query: UrlQueryParams }
    >({
      query: ({ query }) => ({
        url: customersUrlBuilder.getCustomers({ query }),
        method: "GET",
      }),
      transformResponse: (response: GetCustomersResponseDto) =>
        customersMapper(response),
      providesTags: ["customers"],
    }),
    getCustomer: build.query<CustomerProfileResponseDto, { id: string }>({
      query: ({ id }) => ({
        url: customersUrlBuilder.getCustomer({ id }),
        method: "GET",
      }),
      transformResponse: (response: CustomerProfileResponseDto) =>
        customerMapper(response),
      providesTags: ["customer"],
    }),
    updateCustomerStatus: build.mutation<void, { id: string; isVip: boolean }>({
      query: ({ id, ...data }) => ({
        url: `${endpoints.users}/${id}`,
        method: "PATCH",
        data,
      }),
    }),
    getCustomerAddresses: build.query<
      GetCustomersAddressResponseDto[],
      { id: string }
    >({
      query: ({ id }) => ({
        url: customersUrlBuilder.getCustomerAddresses({ id }),
        method: "GET",
      }),
    }),
    getCustomerCoupons: build.query<
      GetCustomerCouponsResponseDto,
      { id: string; query: UrlQueryParams }
    >({
      query: ({ id, query }) => ({
        url: customersUrlBuilder.getCustomerCoupons({ id, query }),
        method: "GET",
      }),
      transformResponse: (response: GetCustomerCouponsResponseDto) =>
        couponsMapper(response),
    }),
    getCustomerMenu: build.query<
      GetCustomerMenuResponseDto,
      { id: string; query: UrlQueryParams }
    >({
      query: ({ id, query }) => ({
        url: customersUrlBuilder.getCustomerMenu({ id, query }),
        method: "GET",
      }),
      transformResponse: (response: GetCustomerMenuResponseDto) =>
        customerMenuMapper(response),
    }),
    getCustomerMenuByLocation: build.query<
      GetCustomerMenuResponseDto,
      { id: string; query?: UrlQueryParams }
    >({
      query: ({ id, query }) => ({
        url: customersUrlBuilder.getCustomerMenuByLocation({ id, query }),
        method: "GET",
      }),
      transformResponse: (response: GetCustomerMenuResponseDto) =>
        customerMenuMapper(response),
    }),
    createCustomersReport: build.query<void, { query: UrlQueryParams }>({
      query: ({ query }) => ({
        url: customersUrlBuilder.createCustomersReport({ query }),
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetCustomerMenuQuery,
  useGetCustomerMenuByLocationQuery,
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useGetCustomerQuery,
  useGetCustomerCouponsQuery,
  useLazyGetCustomerCouponsQuery,
  useLazyGetCustomerQuery,
  useLazyGetCustomerAddressesQuery,
  useUpdateCustomerStatusMutation,
  useLazyCreateCustomersReportQuery,
} = customersAPI;
